<template>
  <v-navigation-drawer
    v-model="isOpen"
    right
    fixed
    width="600"
    temporary
    class="massive-drawer"
  >
    <MassiveProcessHeader
      :title="$t('massive.uploadDrawer.title')"
      :description="$t('massive.uploadDrawer.description')"
      @close="closeDrawer"
    />

    <v-container class="pa-4">
      <div v-if="!processResult">
        <transition name="fade" mode="out-in">
          <div v-if="!loading" key="content">
            <MassiveInfoCards />
            <MassiveTemplateDownloadCard @download="downloadTemplate" />
            <MassiveRecommendations />
            <MassiveUploadDropzone
              @file-selected="handleFileSelected"
              @file-removed="handleFileRemoved"
              @error="handleError"
            />
            <div v-if="selectedFile" class="mt-4 text-center">
              <v-btn
                color="primary"
                :loading="loading"
                :disabled="loading"
                @click="processUpload"
              >
                <v-icon left>mdi-upload</v-icon>
                {{ $t('massive.actions.upload') }}
              </v-btn>
            </div>
          </div>
          <div v-else key="loading" class="loading-container">
            <v-card class="loading-card" outlined>
              <v-card-text class="pa-8 text-center">
                <v-progress-circular
                  :size="80"
                  :width="8"
                  color="primary"
                  indeterminate
                />
                <div class="text-h6 mt-4">{{ $t('massive.uploadDrawer.processing') }}</div>
                <div class="text-body-2 text--secondary mt-2">
                  {{ $t('massive.uploadDrawer.processingText') }}
                </div>
              </v-card-text>
            </v-card>
          </div>
        </transition>
      </div>

      <div v-else class="process-result">
        <v-card class="result-header-card mb-4" outlined elevation="2">
          <v-card-text class="pa-6">
            <div class="d-flex align-center">
              <v-icon
                size="56"
                :color="alertType === 'success' ? 'teal' : 'orange darken-1'"
                class="mr-4"
              >
                {{ alertIcon }}
              </v-icon>
              <div>
                <div class="text-h5 font-weight-medium">{{ title }}</div>
                <div class="text-body-1 mt-2 text--secondary">{{ message }}</div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <v-row class="mb-4">
          <v-col cols="6">
            <v-card class="summary-card" outlined elevation="1">
              <v-card-text class="pa-4">
                <div class="d-flex align-center justify-space-around">
                  <div class="text-center">
                    <v-icon size="48" color="teal">mdi-check-circle</v-icon>
                    <div class="text-h4 mt-2">{{ processResult.createdRows || 0 }}</div>
                    <div class="text-body-2">{{ $t('massive.uploadDrawer.successfulRecords') }}</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="6">
            <v-card class="summary-card" outlined elevation="1">
              <v-card-text class="pa-4">
                <div class="d-flex align-center justify-space-around">
                  <div class="text-center">
                    <v-icon size="48" color="orange darken-1">mdi-alert-circle</v-icon>
                    <div class="text-h4 mt-2">{{ processResult.failedRows || 0 }}</div>
                    <div class="text-body-2">{{ $t('massive.uploadDrawer.errorRecords') }}</div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <MassiveValidationTable
          v-if="errors && errors.length > 0"
          :errors="errors"
          :loading="false"
        />

        <v-row class="mt-6">
          <v-col cols="6">
            <v-btn
              block
              outlined
              color="grey"
              @click="closeDrawer"
            >
              {{ $t('massive.uploadDrawer.close') }}
            </v-btn>
          </v-col>
          <v-col cols="6">
            <v-btn
              block
              color="primary"
              @click="resetProcess"
            >
              {{ $t('massive.uploadDrawer.newProcess') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </v-container>
  </v-navigation-drawer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { MASSIVE_M } from '@/views/users/store/massive/types.js';
import {
  ACTION_MASSIVE_UPLOAD,
  ACTION_DOWNLOAD_TEMPLATE,
  ACTION_RESET_PROCESS
} from '@/views/users/store/massive/actions.js';
import {
  GET_PROCESS_RESULT,
  GET_ERRORS
} from '@/views/users/store/massive/getters.js';
import Swal from 'sweetalert2';

import MassiveProcessHeader from '../components/MassiveProcessHeader.vue';
import MassiveUploadDropzone from '../components/MassiveUploadDropzone.vue';
import MassiveValidationTable from '../components/MassiveValidationTable.vue';
import MassiveInfoCards from '../components/MassiveInfoCards.vue';
import MassiveTemplateDownloadCard from '../components/MassiveTemplateDownloadCard.vue';
import MassiveRecommendations from '../components/MassiveRecommendations.vue';

export default {
  name: 'UserMassiveUploadDrawer',
  components: {
    MassiveProcessHeader,
    MassiveUploadDropzone,
    MassiveValidationTable,
    MassiveInfoCards,
    MassiveTemplateDownloadCard,
    MassiveRecommendations
  },
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedFile: null,
      loading: false,
      errorHeaders: [
        { text: 'Fila', value: 'row', align: 'center', class: 'primary--text font-weight-medium' },
        { text: 'Campo', value: 'field', align: 'center', class: 'primary--text font-weight-medium' },
        { text: 'Error', value: 'message', align: 'left', class: 'primary--text font-weight-medium' }
      ]
    };
  },
  computed: {
    isOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    ...mapGetters({
      processResult: `${MASSIVE_M}${GET_PROCESS_RESULT}`,
      errors: `${MASSIVE_M}${GET_ERRORS}`
    }),
    alertType() {
      if (this.processResult?.failedRows > 0) {
        return 'warning';
      }
      return 'success';
    },
    alertIcon() {
      if (this.processResult?.failedRows > 0) {
        return 'mdi-alert-circle';
      }
      return 'mdi-check-circle';
    },
    title() {
      if (this.processResult?.failedRows > 0) {
        return this.$t('massive.uploadDrawer.partiallyCompleted');
      }
      return this.$t('massive.uploadDrawer.successfullyCompleted');
    },
    message() {
      if (this.processResult?.failedRows > 0) {
        return this.processResult.message || this.$t('massive.uploadDrawer.someRecordsFailed');
      }
      return this.processResult.message || this.$t('massive.uploadDrawer.allRecordsProcessed');
    }
  },
  methods: {
    ...mapActions({
      massiveUpload: `${MASSIVE_M}${ACTION_MASSIVE_UPLOAD}`,
      downloadTemplateAction: `${MASSIVE_M}${ACTION_DOWNLOAD_TEMPLATE}`,
      resetProcessAction: `${MASSIVE_M}${ACTION_RESET_PROCESS}`
    }),
    closeDrawer() {
      this.isOpen = false;
      this.resetProcess();
    },
    handleFileSelected(file) {
      this.selectedFile = file;
    },
    handleFileRemoved() {
      this.selectedFile = null;
    },
    handleError(message) {
      Swal.fire({
        icon: 'error',
        title: this.$t('massive.uploadDrawer.error'),
        text: message,
        confirmButtonColor: '#6E17A5'
      });
    },
    async processUpload() {
      if (!this.selectedFile) {
        Swal.fire({
          icon: 'warning',
          title: this.$t('massive.uploadDrawer.fileNotSelected'),
          text: this.$t('massive.uploadDrawer.selectFileFirst'),
          confirmButtonColor: '#6E17A5'
        });
        return;
      }

      this.loading = true;

      try {
        const idGroup = this.$route.params.idGroup || 'default';
        const idEmpresa = sessionStorage.getItem("idEmpresa");

        if (!idEmpresa) {
          Swal.fire({
            icon: 'error',
            title: this.$t('massive.uploadDrawer.error'),
            text: this.$t('massive.uploadDrawer.idEmpresaError'),
            confirmButtonColor: '#6E17A5'
          });
          return;
        }

        await this.massiveUpload({
          idGroup,
          idEmpresa,
          file: this.selectedFile
        });
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: this.$t('massive.uploadDrawer.error'),
          text: error.message,
          confirmButtonColor: '#6E17A5'
        });
      } finally {
        this.loading = false;
      }
    },
    downloadTemplate() {
      const idEmpresa = sessionStorage.getItem("idEmpresa");
      if (!idEmpresa) {
        Swal.fire({
          icon: 'error',
          title: this.$t('massive.uploadDrawer.error'),
          text: this.$t('massive.uploadDrawer.idEmpresaError'),
          confirmButtonColor: '#6E17A5'
        });
        return;
      }
      this.downloadTemplateAction({ idEmpresa });
    },
    resetProcess() {
      this.selectedFile = null;
      this.resetProcessAction();
    }
  }
};
</script>

<style lang="css" scoped>
.massive-drawer {
  z-index: 1000;
}

.loading-container {
  padding: 20px 0;
}

.loading-card {
  background-color: rgba(110, 23, 165, 0.03);
}

.result-header-card {
  border-left: 4px solid var(--v-primary-base);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.result-header-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}

.result-header-card .text-h6,
.result-header-card .text-subtitle-1,
.result-header-card .text-body-2 {
  word-break: break-word;
  line-height: 1.4;
}

.summary-card {
  height: 100%;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.summary-card .text-body-2 {
  word-break: break-word;
  line-height: 1.4;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
